import { FormEventHandler, useMemo } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  FieldErrors,
  SubmitHandler,
  UseFormReturn,
  useForm,
} from "react-hook-form";
import { useLocation, useNavigate } from "react-router";
import { isErrorMessage } from "@sscs/types";
import { Toast } from "@sscs/ui";
import { maybeNavigateLocation } from "Utils";
import * as yup from "Utils/yup";
import { useLoginMutation } from "../services/auth.api";
import { LoginRequest } from "../types";

type LoginFormData = LoginRequest;

type UseLoginForm = {
  errors: FieldErrors<LoginFormData>;
  isLoginError: boolean;
  methods: UseFormReturn<LoginFormData>;
  onSubmit: FormEventHandler<HTMLFormElement>;
};

const isInvalidCredentials = (error: unknown): boolean =>
  isErrorMessage(error) && error.status === 401;

const schema: yup.SchemaOf<LoginFormData> = yup.object({
  email: yup.string().required(),
  password: yup.string().required(),
});

const useLocalLogin = (): UseLoginForm => {
  const navigate = useNavigate();
  const location = useLocation();

  const navigateTo = maybeNavigateLocation(location.state);

  const defaultValues = useMemo(
    () => ({
      email: "",
      password: "",
    }),
    [],
  );

  const [login, { error: loginError }] = useLoginMutation();

  const methods = useForm<LoginFormData>({
    resolver: yupResolver(schema),
    mode: "onSubmit",
    defaultValues,
  });

  const {
    formState: { errors, isDirty },
    reset,
    handleSubmit,
  } = methods;

  const onSubmit: SubmitHandler<LoginFormData> = async (data) => {
    try {
      await login({
        email: data.email,
        password: data.password,
      }).unwrap();

      if (navigateTo)
        navigate(navigateTo, {
          replace: true,
        });
    } catch (error) {
      if (isInvalidCredentials(error)) {
        // The rejected credentials become the new baseline, so the form is
        // clean again and the banner below shows until the user edits a field.
        reset(data);
      } else {
        Toast.show({
          title: isErrorMessage(error) ? error.data.error : "Unable to login",
          type: "error",
        });
      }
    }
  };

  return {
    errors,
    isLoginError: isInvalidCredentials(loginError) && !isDirty,
    methods,
    onSubmit: handleSubmit(onSubmit),
  };
};
export default useLocalLogin;
